import { useAdminClaimRequests, useAdminApproveClaimRequest, useAdminDenyClaimRequest } from '../../hooks/useAdmin'
import type { AdminClaimRequest } from '../../hooks/useAdmin'

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

function RequestRow({ req }: { req: AdminClaimRequest }) {
  const approve = useAdminApproveClaimRequest()
  const deny = useAdminDenyClaimRequest()
  const busy = approve.isPending || deny.isPending

  return (
    <tr className="border-b border-line-soft">
      <td className="px-4 py-3">
        <div className="font-medium text-ink">{req.churchName}</div>
        <div className="text-[12px] text-muted mt-0.5">{req.churchId}</div>
      </td>
      <td className="px-4 py-3">
        <div className="text-ink">{req.userDisplayName}</div>
        <div className="text-[12px] text-muted">{req.userEmail}</div>
      </td>
      <td className="px-4 py-3">
        {req.churchEmail && <div className="text-[12px] text-ink-2">Email: {req.churchEmail}</div>}
        {req.churchWebsiteUrl && <div className="text-[12px] text-ink-2">Web: {req.churchWebsiteUrl}</div>}
        {!req.churchEmail && !req.churchWebsiteUrl && <span className="text-[12px] text-muted">None on file</span>}
      </td>
      <td className="px-4 py-3 text-muted text-[12px]">{formatDate(req.createdAt)}</td>
      <td className="px-4 py-3">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => approve.mutate(req.id)}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-[12px] font-semibold border-0 cursor-pointer disabled:opacity-50 font-sans"
          >
            Approve
          </button>
          <button
            type="button"
            onClick={() => deny.mutate(req.id)}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg border border-line text-ink text-[12px] font-medium cursor-pointer disabled:opacity-50 font-sans bg-surface"
          >
            Deny
          </button>
        </div>
      </td>
    </tr>
  )
}

export default function AdminClaimRequestsPage() {
  const { data, isLoading } = useAdminClaimRequests()
  const requests = data?.data ?? []

  return (
    <div>
      <div className="mb-6">
        <h1 className="m-0 text-[24px] font-bold">Claim Requests</h1>
        <p className="text-[13px] text-muted mt-2 m-0 max-w-[560px] leading-[1.5]">
          Churches claimed by users whose email/domain couldn't be automatically verified. Review and approve or deny each request.
        </p>
        {!isLoading && <div className="text-[13px] text-muted mt-1">{requests.length} pending</div>}
      </div>

      {isLoading ? (
        <div className="text-muted text-[14px] py-10">Loading…</div>
      ) : requests.length === 0 ? (
        <div className="text-muted text-[14px] py-10">No pending claim requests.</div>
      ) : (
        <div className="bg-surface rounded-[14px] border border-line-soft overflow-hidden">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-line-soft bg-s2">
                {['Church', 'Claimant', 'Church contact info', 'Submitted', 'Action'].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left font-semibold text-ink-2 text-[12px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <RequestRow key={req.id} req={req} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
